import {testIdProps} from '@github-ui/test-id-props'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {useSyncedState} from '@github-ui/use-synced-state'
import {FormControl, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {memo, useCallback, useRef} from 'react'

import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import {Resources} from '../../../strings'
import {EmojiAutocomplete} from '../../common/emoji-autocomplete'

const stopPropagation: React.MouseEventHandler<HTMLInputElement> = e => {
  e.stopPropagation()
}

type Props = {
  /** Whether the dialog is open */
  open: boolean

  /** A function which updates the `open` prop */
  setOpen: (open: boolean) => void

  /** The element to return focus to when the dialog closes */
  anchorRef: React.RefObject<HTMLElement | null>

  /** The current name of the column */
  currentName: string

  /** A callback invoked with the new name when the user saves their changes */
  onRename: (newName: string) => void
}

/**
 * Render a dialog to rename a board column, modeled on the "Rename view" dialog.
 */
export const RenameColumnDialog = memo<Props>(function RenameColumnDialogImpl({
  open,
  setOpen,
  anchorRef,
  currentName,
  onRename,
}) {
  const [localName, setLocalName] = useSyncedState(currentName)
  const {hasWritePermissions} = ViewerPrivileges()
  const inputRef = useRef<HTMLInputElement>(null)

  const resetName = useCallback(() => {
    setLocalName(currentName)
  }, [setLocalName, currentName])

  // Suppress the heading's focus ring for the dialog's return-focus only; the
  // flag is cleared once focus leaves it, so real keyboard focus still shows.
  const markReturnFocus = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    anchor.setAttribute('data-suppress-focus-ring', '')
    anchor.addEventListener('blur', () => anchor.removeAttribute('data-suppress-focus-ring'), {once: true})
  }, [anchorRef])

  const closeDialog = useCallback(() => {
    resetName()
    markReturnFocus()
    setOpen(false)
  }, [resetName, markReturnFocus, setOpen])

  const isValid = localName.trim().length > 0

  const submit = useCallback(() => {
    if (!isValid || localName === currentName) return
    onRename(localName)
    markReturnFocus()
    setOpen(false)
  }, [isValid, localName, currentName, onRename, markReturnFocus, setOpen])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (event.key) {
        case 'Enter': {
          submit()
          event.preventDefault()
          break
        }
        case 'Escape': {
          resetName()
          break
        }
      }
    },
    [submit, resetName],
  )
  const inputCompositionProps = useIgnoreKeyboardActionsWhileComposing(onKeyDown)

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalName(e.target.value)
    },
    [setLocalName],
  )

  if (!open) return null

  return (
    <Dialog
      title={Resources.renameColumnTitle}
      width="small"
      initialFocusRef={inputRef}
      returnFocusRef={anchorRef}
      onClose={closeDialog}
      footerButtons={[
        {
          content: Resources.cancelChanges,
          onClick: e => {
            e.preventDefault()
            closeDialog()
          },
        },
        {
          content: Resources.saveChanges,
          buttonType: 'primary',
          onClick: e => {
            e.preventDefault()
            submit()
          },
        },
      ]}
    >
      <FormControl>
        <FormControl.Label>{Resources.columnNameLabel}</FormControl.Label>
        <EmojiAutocomplete fullWidth>
          <TextInput
            ref={inputRef}
            block
            autoComplete="off"
            value={localName}
            onChange={onChange}
            onMouseDown={stopPropagation}
            disabled={!hasWritePermissions}
            {...testIdProps('board-view-rename-column-input')}
            {...inputCompositionProps}
          />
        </EmojiAutocomplete>
        {!isValid ? (
          <FormControl.Validation variant="error">{Resources.columnNameRequired}</FormControl.Validation>
        ) : null}
      </FormControl>
    </Dialog>
  )
})
